import {
    AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges,
    OnDestroy, Output, SimpleChanges, ViewChild
} from "@angular/core";
import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { TableKit } from '@tiptap/extension-table';
import TextAlign from '@tiptap/extension-text-align';

/*
 * Rich text editor.
 *
 * Replaces TinyMCE. TinyMCE 6.8.6 is the final 6.x release and carries five
 * unpatched XSS advisories, three at CVSS 8.7; every patched version (7.9.3+,
 * 8.x) is GPL-2.0-or-later or commercial. Because the editor is bundled into the
 * application's own script bundle and shipped to every browser, adopting GPL
 * would arguably reach the combined work. Tiptap is MIT, actively maintained,
 * and has an official table extension, so this removes both the advisories and
 * the licensing exposure rather than trading one for the other.
 *
 * The contract for callers is unchanged: same inputs, and the same
 * onEditorKeyup output emitting an HTML string. The one deliberate difference is
 * that content insertion is now a method on this component (insertHtml) instead
 * of reaching through a global tinymce registry by element id.
 */
@Component({
    selector: 'app-template-editor',
    template: `
    <div class="tpl-editor" [class.tpl-editor-readonly]="disableItem">
      @if (!disableItem) {
        <div class="tpl-toolbar btn-toolbar" role="toolbar">
          <div class="btn-group btn-group-sm me-2">
            <select class="form-select form-select-sm tpl-format"
                    [value]="currentBlock()"
                    (change)="setBlock($any($event.target).value)">
              <option value="p">Paragraph</option>
              <option value="h1">Heading 1</option>
              <option value="h2">Heading 2</option>
              <option value="h3">Heading 3</option>
            </select>
          </div>
          <div class="btn-group btn-group-sm me-2">
            <button type="button" class="btn btn-outline-secondary" title="Bold"
                    [class.active]="isActive('bold')" (click)="run('toggleBold')"><b>B</b></button>
            <button type="button" class="btn btn-outline-secondary" title="Italic"
                    [class.active]="isActive('italic')" (click)="run('toggleItalic')"><i>I</i></button>
            <button type="button" class="btn btn-outline-secondary" title="Strikethrough"
                    [class.active]="isActive('strike')" (click)="run('toggleStrike')"><s>S</s></button>
            <button type="button" class="btn btn-outline-secondary" title="Underline"
                    [class.active]="isActive('underline')" (click)="run('toggleUnderline')"><u>U</u></button>
          </div>
          <div class="btn-group btn-group-sm me-2">
            <button type="button" class="btn btn-outline-secondary" title="Link"
                    [class.active]="isActive('link')" (click)="setLink()">&#128279;</button>
            <button type="button" class="btn btn-outline-secondary" title="Remove link"
                    (click)="run('unsetLink')">&#9986;</button>
            <button type="button" class="btn btn-outline-secondary" title="Image"
                    (click)="insertImage()">&#128247;</button>
          </div>
          <div class="btn-group btn-group-sm me-2">
            <button type="button" class="btn btn-outline-secondary" title="Bullet list"
                    [class.active]="isActive('bulletList')" (click)="run('toggleBulletList')">&#8226;</button>
            <button type="button" class="btn btn-outline-secondary" title="Numbered list"
                    [class.active]="isActive('orderedList')" (click)="run('toggleOrderedList')">1.</button>
            <button type="button" class="btn btn-outline-secondary" title="Outdent"
                    (click)="outdent()">&#8676;</button>
            <button type="button" class="btn btn-outline-secondary" title="Indent"
                    (click)="indent()">&#8677;</button>
          </div>
          <div class="btn-group btn-group-sm me-2">
            <button type="button" class="btn btn-outline-secondary" title="Align left"
                    [class.active]="isAlign('left')" (click)="setAlign('left')">L</button>
            <button type="button" class="btn btn-outline-secondary" title="Align centre"
                    [class.active]="isAlign('center')" (click)="setAlign('center')">C</button>
            <button type="button" class="btn btn-outline-secondary" title="Align right"
                    [class.active]="isAlign('right')" (click)="setAlign('right')">R</button>
            <button type="button" class="btn btn-outline-secondary" title="Justify"
                    [class.active]="isAlign('justify')" (click)="setAlign('justify')">J</button>
          </div>
          <div class="btn-group btn-group-sm me-2">
            <button type="button" class="btn btn-outline-secondary" title="Insert table"
                    (click)="insertTable()">&#9638;</button>
            <button type="button" class="btn btn-outline-secondary" title="Add row"
                    (click)="run('addRowAfter')">+R</button>
            <button type="button" class="btn btn-outline-secondary" title="Add column"
                    (click)="run('addColumnAfter')">+C</button>
            <button type="button" class="btn btn-outline-secondary" title="Delete table"
                    (click)="run('deleteTable')">&#10006;</button>
          </div>
          <div class="btn-group btn-group-sm">
            <button type="button" class="btn btn-outline-secondary" title="Code block"
                    [class.active]="isActive('codeBlock')" (click)="run('toggleCodeBlock')">&lt;/&gt;</button>
            <button type="button" class="btn btn-outline-secondary" title="Clear formatting"
                    (click)="clearFormatting()">Tx</button>
          </div>
        </div>
      }
      <div #editorHost class="tpl-content" [style.height]="height"></div>
    </div>
  `,
    styles: [`
    .tpl-editor { border: 1px solid #dee2e6; border-radius: 4px; background: #fff; }
    .tpl-toolbar { padding: 6px; border-bottom: 1px solid #dee2e6; flex-wrap: wrap; gap: 4px 0; }
    .tpl-format { min-width: 120px; }
    .tpl-content { overflow-y: auto; padding: 10px; }
    .tpl-editor-readonly .tpl-content { background: #f8f9fa; }
  `],
    standalone: false
})
export class TemplateEditor implements AfterViewInit, OnDestroy, OnChanges {

    @Input() elementId: string;
    @Input() tempValue: string;
    @Output() onEditorKeyup = new EventEmitter<any>();
    @Input() height;
    @Input() disableItem = false;

    @ViewChild('editorHost') editorHost: ElementRef<HTMLElement>;

    private editor: Editor | null = null;

    ngAfterViewInit() {
        this.editor = new Editor({
            element: this.editorHost.nativeElement,
            editable: !this.disableItem,
            extensions: [
                StarterKit.configure({
                    link: { openOnClick: false }
                }),
                Image,
                TableKit.configure({ table: { resizable: true } }),
                TextAlign.configure({ types: ['heading', 'paragraph'] })
            ],
            content: this.tempValue || '',
            onUpdate: ({ editor }) => {
                this.onEditorKeyup.emit(editor.getHTML());
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.editor) {
            return;
        }
        if (changes['disableItem']) {
            this.editor.setEditable(!this.disableItem);
        }
        // Only push external content in when it actually changed, otherwise a
        // round-trip through the parent would reset the caret on every keystroke.
        if (changes['tempValue'] && !changes['tempValue'].firstChange) {
            const incoming = this.tempValue || '';
            if (incoming !== this.editor.getHTML()) {
                this.editor.commands.setContent(incoming, { emitUpdate: false });
            }
        }
    }

    ngOnDestroy() {
        this.editor?.destroy();
        this.editor = null;
    }

    // Public API used by help-editor to drop a document link at the caret.
    // Replaces the old tinymce.get(elementId).insertContent(...) global lookup.
    insertHtml(html: string): void {
        this.editor?.chain().focus().insertContent(html).run();
    }

    getHtml(): string {
        return this.editor?.getHTML() ?? '';
    }

    isActive(name: string): boolean {
        return !!this.editor?.isActive(name);
    }

    isAlign(align: string): boolean {
        return !!this.editor?.isActive({ textAlign: align });
    }

    currentBlock(): string {
        if (!this.editor) {
            return 'p';
        }
        for (const level of [1, 2, 3]) {
            if (this.editor.isActive('heading', { level })) {
                return 'h' + level;
            }
        }
        return 'p';
    }

    setBlock(value: string): void {
        if (!this.editor) {
            return;
        }
        if (value === 'p') {
            this.editor.chain().focus().setParagraph().run();
        } else {
            const level = Number(value.substring(1)) as 1 | 2 | 3;
            this.editor.chain().focus().toggleHeading({ level }).run();
        }
    }

    setAlign(align: string): void {
        this.editor?.chain().focus().setTextAlign(align).run();
    }

    indent(): void {
        this.editor?.chain().focus().sinkListItem('listItem').run();
    }

    outdent(): void {
        this.editor?.chain().focus().liftListItem('listItem').run();
    }

    setLink(): void {
        if (!this.editor) {
            return;
        }
        const previous = this.editor.getAttributes('link')['href'] || '';
        const href = window.prompt('Link URL', previous);
        if (href === null) {
            return;
        }
        if (href === '') {
            this.editor.chain().focus().unsetLink().run();
            return;
        }
        this.editor.chain().focus().setLink({ href }).run();
    }

    insertImage(): void {
        const src = window.prompt('Image URL', '');
        if (src) {
            this.editor?.chain().focus().setImage({ src }).run();
        }
    }

    insertTable(): void {
        this.editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
    }

    clearFormatting(): void {
        this.editor?.chain().focus().unsetAllMarks().clearNodes().run();
    }

    // Passthrough for the no-argument toolbar commands.
    run(command: string): void {
        const chain = this.editor?.chain().focus() as unknown as Record<string, () => { run: () => void }>;
        if (chain && typeof chain[command] === 'function') {
            chain[command]().run();
        }
    }
}
